import { apiClient } from './client'
import type {
  Lesson,
  LessonParticipant,
  LessonPriceCalculation,
  LessonScope,
  LessonStatus,
} from '@/types'
import { API_ENDPOINTS } from '@/utils/constants'
import type { PaginatedResponse } from '@/types'

type LessonListParams = {
  from?: string
  to?: string
  lesson_scope?: LessonScope
  recipient_id?: number
  teacher_id?: number
  status?: LessonStatus[]
  per_page?: number
  page?: number
  /** Только уроки с участием текущего пользователя (обязательно для UI ученика при роли teacher+student) */
  as_student?: boolean
}

type ApiResponse<T> = {
  success?: boolean
  message?: string
  data?: T
}

/** Laravel AnonymousResourceCollection + paginate: data + meta (без legacy top-level current_page). */
type IndexResponse<T> = {
  data: T[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

function unwrapPaginated<T>(raw: IndexResponse<T>): PaginatedResponse<T> {
  return {
    data: raw.data,
    current_page: raw.meta.current_page,
    last_page: raw.meta.last_page,
    per_page: raw.meta.per_page,
    total: raw.meta.total,
  }
}

function unwrapResource<T>(raw: ApiResponse<T> | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiResponse<T>).data as T
  }
  return raw as T
}

type LessonParticipantInput = {
  user_id: number
}

type LessonCreatePayload = {
  lesson_scope: LessonScope
  recipient_id: number
  planned_start_at: string
  planned_end_at: string
  participants: LessonParticipantInput[]
}

export const lessonsApi = {
  index: async (params: LessonListParams = {}): Promise<PaginatedResponse<Lesson>> => {
    const query: Record<string, unknown> = { ...params }

    // Laravel paginate expects `status[]=...` for array params; axios needs explicit serializer.
    const response = await apiClient.get<IndexResponse<Lesson>>('/lessons', {
      params: query,
      paramsSerializer: {
        indexes: null,
        serialize: (p: Record<string, unknown>) => {
          const searchParams = new URLSearchParams()
          Object.keys(p).forEach((key) => {
            const value = p[key]
            if (value === undefined || value === null) return
            if (Array.isArray(value)) {
                // Laravel validates `status` as an array (`status` => ['array']), so we must send `status[]`.
                value.forEach((item) => searchParams.append(`${key}[]`, String(item)))
              return
            }
            searchParams.append(key, String(value))
          })
          return searchParams.toString()
        },
      },
    })

    return unwrapPaginated(response.data)
  },

  getById: async (lessonId: number): Promise<Lesson> => {
    const response = await apiClient.get<Lesson | { data?: Lesson }>(`/lessons/${lessonId}`)
    // Laravel Resource for show usually returns the object directly.
    return unwrapResource<Lesson>(response.data)
  },

  create: async (payload: LessonCreatePayload): Promise<Lesson> => {
    const response = await apiClient.post<ApiResponse<Lesson>>('/lessons', payload)
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка создания урока')
  },

  start: async (lessonId: number): Promise<Lesson> => {
    const response = await apiClient.post<ApiResponse<Lesson>>(`/lessons/${lessonId}/start`, {})
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка запуска урока')
  },

  finish: async (
    lessonId: number
  ): Promise<{
    lesson: Lesson
    openHomeworkModal: boolean
  }> => {
    const response = await apiClient.post<{
      message?: string
      open_homework_modal?: boolean
      data?: Lesson
    }>(`/lessons/${lessonId}/finish`, {})

    if (response.data?.data) {
      return {
        lesson: response.data.data,
        openHomeworkModal: Boolean(response.data.open_homework_modal),
      }
    }

    throw new Error(response.data?.message || 'Ошибка завершения урока')
  },

  cancel: async (lessonId: number, cancelReason?: string): Promise<Lesson> => {
    const response = await apiClient.post<ApiResponse<Lesson>>(`/lessons/${lessonId}/cancel`, {
      cancel_reason: cancelReason ?? null,
    })
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка отмены урока')
  },

  reschedule: async (
    lessonId: number,
    payload: { planned_start_at: string; planned_end_at: string; reason?: string | null },
  ): Promise<Lesson> => {
    const response = await apiClient.post<ApiResponse<Lesson>>(`/lessons/${lessonId}/reschedule`, payload)
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка переноса урока')
  },

  getParticipants: async (lessonId: number): Promise<LessonParticipant[]> => {
    const response = await apiClient.get<IndexResponse<LessonParticipant>>(
      `/lessons/${lessonId}/participants`
    )

    const raw = response.data
    if (Array.isArray(raw)) return raw
    if (raw?.data && Array.isArray(raw.data)) return raw.data
    return []
  },

  replaceParticipants: async (lessonId: number, participants: LessonParticipantInput[]): Promise<LessonParticipant[]> => {
    const response = await apiClient.put<{
      message?: string
      data?: LessonParticipant[]
    }>(`/lessons/${lessonId}/participants`, {
      participants,
    })

    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка обновления участников урока')
  },

  update: async (
    lessonId: number,
    payload: {
      planned_start_at?: string
      planned_end_at?: string
      cancel_reason?: string | null
    },
  ): Promise<Lesson> => {
    const response = await apiClient.patch<ApiResponse<Lesson>>(`/lessons/${lessonId}`, payload)
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка обновления урока')
  },

  calculatePrice: async (payload: {
    recipient_type: 'user' | 'group'
    recipient_id: number
    planned_start_at: string
    planned_end_at: string
  }): Promise<LessonPriceCalculation> => {
    const response = await apiClient.post<{ data?: LessonPriceCalculation }>(
      API_ENDPOINTS.LESSON_API.CALCULATE_PRICE,
      payload
    )
    if (response.data?.data) return response.data.data
    throw new Error('Ошибка расчёта цены урока')
  },
}

